import { apiFetch, ApiError } from './api';
import { setUser, ensureCurrentTenantId, getCurrentTenantId, setCurrentTenantId, isTenantAdmin, type CurrentUser } from './state';
import { startRouter } from './router';

import './views/dashboard';
import './views/campaigns/list';
import './views/contacts';
import './views/templates';
import './views/providers';
import './views/webhooks';
import './views/apiKeys';
import './views/users';
import './views/auditLogs';
import './views/roles';
import './views/tenantMembers';
import './views/tenants';

interface TenantOption {
  id: string;
  name: string;
}

async function loadTenantOptions(user: CurrentUser): Promise<TenantOption[]> {
  // Super-admins can act within any tenant (support access), not just ones they're a member of —
  // so their switcher lists every tenant, fetched fresh, rather than just user.tenants.
  if (user.isSuperAdmin) {
    const all = await apiFetch<{ id: string; name: string }[]>('/api/tenants').catch(() => []);
    return all.map((t) => ({ id: t.id, name: t.name }));
  }
  return user.tenants.map((t) => ({ id: t.tenantId, name: t.tenantName }));
}

function renderTenantSwitcher(options: TenantOption[]): void {
  const wrap = document.getElementById('tenant-switcher-wrap');
  if (!wrap) return;

  // Only one tenant available (true for every current single-tenant user) — nothing to switch.
  if (options.length <= 1) {
    wrap.classList.add('hidden');
    return;
  }

  wrap.classList.remove('hidden');
  const select = wrap.querySelector<HTMLSelectElement>('#tenant-switcher')!;
  const current = getCurrentTenantId();
  select.innerHTML = options.map((t) => `<option value="${t.id}" ${t.id === current ? 'selected' : ''}>${t.name}</option>`).join('');

  select.addEventListener('change', () => {
    setCurrentTenantId(select.value);
    location.reload(); // simplest correct way to re-scope every already-loaded view's data
  });
}

async function init(): Promise<void> {
  let user: CurrentUser;
  try {
    user = await apiFetch<CurrentUser>('/api/auth/me');
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) {
      location.href = 'login.html';
      return;
    }
    throw err;
  }

  setUser(user);
  ensureCurrentTenantId(user);

  document.getElementById('user-name')!.textContent = user.name;
  document.getElementById('user-role')!.textContent = user.tenant?.roleName ?? user.role;

  const tenantAdmin = isTenantAdmin();

  const navUsers = document.getElementById('nav-users');
  if (navUsers && !tenantAdmin) navUsers.classList.add('hidden');

  // Audit logs are cross-tenant now (Phase 2) — gated on super-admin, not "any tenant's admin".
  const navAuditLogs = document.getElementById('nav-audit-logs');
  if (navAuditLogs && !user.isSuperAdmin) navAuditLogs.classList.add('hidden');

  const navRoles = document.getElementById('nav-roles');
  if (navRoles && !tenantAdmin) navRoles.classList.add('hidden');

  const navMembers = document.getElementById('nav-members');
  if (navMembers && !tenantAdmin) navMembers.classList.add('hidden');

  const navTenants = document.getElementById('nav-tenants');
  if (navTenants && !user.isSuperAdmin) navTenants.classList.add('hidden');

  // Operator has no providers:read/webhooks:read grant — match the server's RBAC in the UI.
  const currentRole = user.tenant?.roleName ?? user.role;
  const navProviders = document.getElementById('nav-providers');
  if (navProviders && currentRole === 'operator') navProviders.classList.add('hidden');

  const navWebhooks = document.getElementById('nav-webhooks');
  if (navWebhooks && currentRole === 'operator') navWebhooks.classList.add('hidden');

  loadTenantOptions(user)
    .then(renderTenantSwitcher)
    .catch(() => document.getElementById('tenant-switcher-wrap')?.classList.add('hidden'));

  const content = document.getElementById('content')!;
  const title = document.getElementById('topbar-title')!;
  const navItems = Array.from(document.querySelectorAll<HTMLAnchorElement>('.nav-item'));
  const sidebar = document.getElementById('sidebar');

  startRouter(content, (route) => {
    title.textContent = route.title;
    navItems.forEach((el) => el.classList.toggle('is-active', el.dataset.route === route.path));
    sidebar?.classList.remove('is-open');
  });

  document.getElementById('logout-btn')?.addEventListener('click', async () => {
    await apiFetch('/api/auth/logout', { method: 'POST' }).catch(() => undefined);
    location.href = 'login.html';
  });

  document.getElementById('sidebar-toggle')?.addEventListener('click', () => {
    sidebar?.classList.toggle('is-open');
  });
}

void init();
